import { DateTime } from "luxon";
import { Appointment } from "../models/admissions.model";
import { AddActivityNotePayload } from "../models/activity-notes.model";
import { ProcedureRoomListing } from "../models/procedure-room.model";
import { resolveProcedureRoomScheduleDate } from "./procedure-room-auto-check-in.util";

/** PHI activities that are not scoped to a single encounter DOS. */
const PATIENT_LEVEL_ACTIVITY_TYPES = new Set(
  [
    "form_filled",
    "sms",
    "inbound",
    "outbound",
    "missed",
    "missed_call",
    "voicemail",
    "referral created",
    "coverage - primary",
  ].map((t) => t.toLowerCase())
);

export function isPatientLevelActivityType(
  activityType: string | null | undefined
): boolean {
  return PATIENT_LEVEL_ACTIVITY_TYPES.has(
    String(activityType ?? "")
      .trim()
      .toLowerCase()
  );
}

export type ActivityEncounterSource = {
  procedureListing?: Partial<ProcedureRoomListing> | null;
  appointment?: Partial<Appointment> | null;
  procedure?: { id?: string; encounterId?: string } | null;
};

/** Normalize schedule / encounter datetime to `yyyy-MM-dd` for activity `selectedDate`. */
export function normalizeActivityEncounterDosIso(
  value?: string | null
): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }
  const dateOnly = trimmed.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return dateOnly;
  }
  const dt = DateTime.fromISO(trimmed);
  if (dt.isValid) {
    return dt.toISODate() || "";
  }
  for (const fmt of ["MM/dd/yyyy", "M/d/yyyy", "MM/dd/yy", "M/d/yy"]) {
    const parsed = DateTime.fromFormat(trimmed, fmt);
    if (parsed.isValid) {
      return parsed.toISODate() || "";
    }
  }
  return "";
}

/** Resolve DOS for the current chart context (appointment / listing). */
export function resolveActivityEncounterDosForSource(
  source: ActivityEncounterSource
): string {
  const encounterId = resolveActivityEncounterId(source);
  if (!encounterId) {
    return "";
  }
  return resolveActivityEncounterDosIso(encounterId, source);
}

export function resolveActivityEncounterId(
  source: ActivityEncounterSource
): string {
  return String(
    source.procedureListing?.encounterId ||
      source.appointment?.id ||
      source.procedure?.encounterId ||
      ""
  ).trim();
}

export function resolveActivityEncounterDosIso(
  encounterId: string,
  source: ActivityEncounterSource
): string {
  const eid = String(encounterId || "").trim();
  if (!eid) {
    return "";
  }

  const listing = source.procedureListing;
  const appt = source.appointment;

  if (appt && String(appt.id || "").trim() === eid) {
    return resolveProcedureRoomScheduleDate(
      listing as ProcedureRoomListing,
      appt as Appointment
    );
  }

  if (listing && String(listing.encounterId || "").trim() === eid) {
    return resolveProcedureRoomScheduleDate(
      listing as ProcedureRoomListing,
      appt as Appointment
    );
  }

  return resolveProcedureRoomScheduleDate(
    listing as ProcedureRoomListing,
    appt as Appointment
  );
}

/** Activity tile header: `Author · DOS MM/dd/yyyy`. */
export function formatActivityNoteTitleWithDos(
  activityBy: string | null | undefined,
  dosDisplay: string | null | undefined
): string {
  const name = String(activityBy ?? "").trim();
  const dos = String(dosDisplay ?? "").trim();
  if (!name && !dos) {
    return "";
  }
  if (!dos) {
    return name;
  }
  if (!name) {
    return `DOS ${dos}`;
  }
  return `${name} · DOS ${dos}`;
}

/** User-facing DOS label (`MM/dd/yyyy`). */
export function formatActivityEncounterDosDisplay(
  dosIso?: string | null
): string {
  const iso = normalizeActivityEncounterDosIso(dosIso);
  if (!iso) {
    return "";
  }
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("MM/dd/yyyy") : iso;
}

export function chartEncounterActivityPayload(
  payload: AddActivityNotePayload
): AddActivityNotePayload {
  return { ...payload, requireEncounterAssociation: true };
}

export function enrichActivityNotePayloadWithEncounterContext(
  payload: AddActivityNotePayload,
  source: ActivityEncounterSource,
  options?: { scheduleDate?: string; requireEncounterAssociation?: boolean }
): AddActivityNotePayload {
  const next: AddActivityNotePayload = { ...payload };
  const requireAssoc =
    options?.requireEncounterAssociation === true ||
    next.requireEncounterAssociation === true;

  if (!requireAssoc) {
    let dosIso = normalizeActivityEncounterDosIso(next.selectedDate);
    const encounterId = String(next.encounterId || "").trim();
    if (!dosIso && encounterId) {
      dosIso = resolveActivityEncounterDosIso(encounterId, source);
    }
    if (dosIso) {
      next.selectedDate = dosIso;
    }
    return next;
  }

  next.requireEncounterAssociation = true;

  let encounterId = String(next.encounterId || "").trim();
  if (!encounterId) {
    encounterId = resolveActivityEncounterId(source);
    if (encounterId) {
      next.encounterId = encounterId;
    }
  }

  let dosIso = normalizeActivityEncounterDosIso(next.selectedDate);
  if (!dosIso && options?.scheduleDate) {
    dosIso = normalizeActivityEncounterDosIso(options.scheduleDate);
  }
  if (!dosIso && encounterId) {
    dosIso = resolveActivityEncounterDosIso(encounterId, source);
  }
  if (dosIso) {
    next.selectedDate = dosIso;
  }

  return next;
}

export function buildEncounterDosLookupFromActivityNotes(
  notes: readonly { encounterId?: string | null; selectedDate?: string | null }[],
  options?: { defaultEncounterId?: string }
): Map<string, string> {
  const map = new Map<string, string>();
  const defaultEid = String(options?.defaultEncounterId || "").trim();

  for (const note of notes) {
    const dos = normalizeActivityEncounterDosIso(note.selectedDate);
    if (!dos) {
      continue;
    }
    const eid =
      String(note.encounterId || "").trim() ||
      (defaultEid && !String(note.encounterId || "").trim() ? defaultEid : "");
    if (eid && !map.has(eid)) {
      map.set(eid, dos);
    }
  }
  return map;
}

export function resolveActivityNoteEncounterDosDisplay(
  note: { encounterId?: string | null; selectedDate?: string | null },
  source?: ActivityEncounterSource,
  encounterDosById?: ReadonlyMap<string, string>,
  fallbackEncounterId?: string
): string {
  const fromNote = formatActivityEncounterDosDisplay(note.selectedDate);
  if (fromNote) {
    return fromNote;
  }

  const eid = String(note.encounterId || fallbackEncounterId || "").trim();
  if (!eid) {
    return "";
  }

  const fromLookup = encounterDosById?.get(eid);
  if (fromLookup) {
    return formatActivityEncounterDosDisplay(fromLookup);
  }

  if (source) {
    return formatActivityEncounterDosDisplay(
      resolveActivityEncounterDosIso(eid, source)
    );
  }

  return "";
}
